import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { deleteZoomLink, getAllLinks } from "../Pages/constants";
import "./ContenedorLinksZoom.css";

const obtenerListaLinks = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  if (Array.isArray(responseData?.links)) {
    return responseData.links;
  }

  return [];
};

const obtenerTextoUrl = (url) => {
  if (!url) {
    return "Enlace no disponible";
  }

  try {
    const urlNormalizada = new URL(url);
    return `${urlNormalizada.hostname}${urlNormalizada.pathname === "/" ? "" : urlNormalizada.pathname}`;
  } catch {
    return url;
  }
};

const ContenedorLinksZoom = () => {
  const [links, setLinks] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [linkPorEliminar, setLinkPorEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cargarLinks = useCallback(async () => {
    setCargando(true);
    setError("");

    try {
      const response = await axios.get(getAllLinks);
      setLinks(obtenerListaLinks(response.data));
    } catch (error) {
      console.error("Error al obtener los links de Zoom:", error);
      setLinks([]);
      setError("No fue posible cargar los links de Zoom.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarLinks();
  }, [cargarLinks]);

  const confirmarEliminacion = async () => {
    if (!linkPorEliminar) {
      return;
    }

    setEliminando(true);

    try {
      const response = await axios.delete(deleteZoomLink, {
        params: { id_url: linkPorEliminar.id_url },
      });

      setLinks((linksActuales) =>
        linksActuales.filter((link) => link.id_url !== linkPorEliminar.id_url),
      );
      setLinkPorEliminar(null);
      setResultado({
        tipo: "success",
        titulo: "Link eliminado",
        mensaje:
          response.data?.message || "El link de Zoom se eliminó correctamente.",
      });
    } catch (error) {
      console.error(
        "Error al eliminar el link de Zoom:",
        error.response?.data || error.message,
      );
      setLinkPorEliminar(null);
      setResultado({
        tipo: "error",
        titulo: "No se pudo eliminar",
        mensaje:
          error.response?.data?.message ||
          "Ocurrió un error al eliminar el link. Inténtalo nuevamente.",
      });
    } finally {
      setEliminando(false);
    }
  };

  return (
    <section className="zoom-links-panel">
      <div className="zoom-links-header">
        <div>
          <span className="zoom-links-eyebrow">Reuniones programadas</span>
          <h3>Links de Zoom</h3>
          <p>Consulta y administra los accesos de las clases.</p>
        </div>

        <div className="zoom-links-header-actions">
          {!cargando && !error && (
            <span className="zoom-links-count">
              {links.length} {links.length === 1 ? "link" : "links"}
            </span>
          )}
          <button
            className="zoom-links-refresh"
            type="button"
            onClick={cargarLinks}
            disabled={cargando}
          >
            {cargando ? "Cargando..." : "Actualizar"}
          </button>
        </div>
      </div>

      {cargando && (
        <div className="zoom-links-state" role="status">
          <span className="zoom-links-spinner" aria-hidden="true" />
          <strong>Cargando links de Zoom...</strong>
          <p>Estamos consultando las reuniones registradas.</p>
        </div>
      )}

      {!cargando && error && (
        <div className="zoom-links-state zoom-links-state-error" role="alert">
          <span className="zoom-links-state-icon" aria-hidden="true">
            !
          </span>
          <strong>No pudimos cargar los links</strong>
          <p>{error}</p>
          <button type="button" onClick={cargarLinks}>
            Intentar nuevamente
          </button>
        </div>
      )}

      {!cargando && !error && links.length === 0 && (
        <div className="zoom-links-state">
          <span
            className="zoom-links-state-icon zoom-links-state-icon-empty"
            aria-hidden="true"
          >
            0
          </span>
          <strong>No hay links registrados</strong>
          <p>Los links de Zoom que se agreguen aparecerán en esta sección.</p>
        </div>
      )}

      {!cargando && !error && links.length > 0 && (
        <div className="zoom-links-grid">
          {links.map((link, index) => (
            <article
              className="zoom-link-card"
              key={link.id_url || `${link.matricula_clase}-${index}`}
            >
              <div className="zoom-link-card-top">
                <div className="zoom-link-class">
                  <span className="zoom-link-camera" aria-hidden="true">
                    Z
                  </span>
                  <div>
                    <h4>{link.nombre_clase || "Clase sin nombre"}</h4>
                    <span className="zoom-link-matricula">
                      {link.matricula_clase || "Sin matrícula"}
                    </span>
                  </div>
                </div>
                <span className="zoom-link-status">Disponible</span>
              </div>

              <div className="zoom-link-details">
                <div>
                  <span className="zoom-link-detail-label">Fecha</span>
                  <strong>{link.fecha || "Sin fecha"}</strong>
                </div>
                <div>
                  <span className="zoom-link-detail-label">Hora</span>
                  <strong>{link.hora || "Sin hora"}</strong>
                </div>
              </div>

              <div className="zoom-link-address">
                <span className="zoom-link-detail-label">
                  Enlace de reunión
                </span>
                <span title={link.url}>{obtenerTextoUrl(link.url)}</span>
              </div>

              <div className="zoom-link-actions">
                <a
                  className={`zoom-link-open${link.url ? "" : " zoom-link-open-disabled"}`}
                  href={link.url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!link.url}
                >
                  Abrir reunión
                </a>
                <button
                  className="zoom-link-delete"
                  type="button"
                  onClick={() => setLinkPorEliminar(link)}
                >
                  Eliminar
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {linkPorEliminar && (
        <div className="zoom-links-modal-backdrop" role="presentation">
          <div
            className="zoom-links-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="zoom-delete-title"
          >
            <span
              className="zoom-links-modal-icon zoom-links-modal-icon-warning"
              aria-hidden="true"
            >
              !
            </span>
            <h3 id="zoom-delete-title">¿Eliminar este link?</h3>
            <p>
              Se eliminará el acceso de{" "}
              <strong>
                {linkPorEliminar.nombre_clase ||
                  linkPorEliminar.matricula_clase}
              </strong>
              . Esta acción no se puede deshacer.
            </p>
            <div className="zoom-links-modal-actions">
              <button
                type="button"
                onClick={() => setLinkPorEliminar(null)}
                disabled={eliminando}
              >
                Cancelar
              </button>
              <button
                className="zoom-links-modal-primary zoom-links-modal-danger"
                type="button"
                onClick={confirmarEliminacion}
                disabled={eliminando}
              >
                {eliminando ? "Eliminando..." : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resultado && (
        <div className="zoom-links-modal-backdrop" role="presentation">
          <div
            className="zoom-links-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="zoom-result-title"
          >
            <span
              className={`zoom-links-modal-icon zoom-links-modal-icon-${resultado.tipo}`}
              aria-hidden="true"
            >
              {resultado.tipo === "success" ? "✓" : "!"}
            </span>
            <h3 id="zoom-result-title">{resultado.titulo}</h3>
            <p>{resultado.mensaje}</p>
            <div className="zoom-links-modal-actions zoom-links-modal-actions-centered">
              <button
                className="zoom-links-modal-primary"
                type="button"
                onClick={() => setResultado(null)}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContenedorLinksZoom;
